import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronDown, ChevronUp, Star, ArrowLeft, Sparkles } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { useRatings } from '../context/RatingsContext';
import { useRecentlyViewed } from '../context/RecentlyViewedContext';
import ScrollReveal from '../components/ScrollReveal';
import { formatFCFA } from '../lib/currency';
import { publicCurrentUrl } from '../lib/publicUrl';
import { notify } from '../components/BalmartToaster';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const resolveImage = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/')) return `${BACKEND}${url}`;
  return url;
};
const SKIN_TYPE_FR = { 'Oily': 'Grasse', 'Dry': 'S\u00e8che', 'Sensitive': 'Sensible', 'All': 'Tous' };

function formatRelativeDate(iso) {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'à l\u2019instant';
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    if (diff < 2592000) return `il y a ${Math.floor(diff / 86400)} j`;
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return ''; }
}

export default function ProductDetailPage() {
  const { id } = useParams();
  const { addToCart } = useCart();
  const { customer } = useCustomerAuth();
  const { reloadRatings } = useRatings();
  const { track } = useRecentlyViewed();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [showIngredients, setShowIngredients] = useState(false);
  const [reviewForm, setReviewForm] = useState({ reviewer_name: '', rating: 5, comment: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(true);
  const [complements, setComplements] = useState([]);

  useEffect(() => {
    if (id) track(id);
  }, [id, track]);

  // SEO: set page title, meta description, Open Graph, and Product schema.org
  useEffect(() => {
    if (!product) return;
    const shortDesc = (product.description || '').slice(0, 155);
    document.title = `${product.name} · Balmart`;

    const ensureMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
      return el;
    };
    ensureMeta('name', 'description', shortDesc);
    ensureMeta('property', 'og:title', `${product.name} · Balmart`);
    ensureMeta('property', 'og:description', shortDesc);
    ensureMeta('property', 'og:image', product.image_url || '');
    ensureMeta('property', 'og:type', 'product');
    ensureMeta('property', 'og:url', publicCurrentUrl());
    ensureMeta('name', 'twitter:title', `${product.name} · Balmart`);
    ensureMeta('name', 'twitter:description', shortDesc);
    ensureMeta('name', 'twitter:image', product.image_url || '');

    // JSON-LD schema.org Product
    const scriptId = 'product-jsonld';
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    const avgRating = reviews.length > 0 ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length) : 0;
    const jsonLd = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: product.name,
      image: product.image_url,
      description: product.description,
      brand: { '@type': 'Brand', name: 'Balmart' },
      offers: {
        '@type': 'Offer',
        priceCurrency: 'XOF',
        price: product.price,
        availability: (product.stock > 0) ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        url: publicCurrentUrl(),
      },
    };
    if (reviews.length > 0) {
      jsonLd.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: avgRating.toFixed(1),
        reviewCount: reviews.length,
      };
    }
    script.textContent = JSON.stringify(jsonLd);

    return () => {
      // Reset title on unmount
      document.title = 'Balmart';
    };
  }, [product, reviews]);

  useEffect(() => {
    axios.get(`${API}/products/suggest`, { params: { product_id: id, limit: 4 } })
      .then(r => setComplements(r.data || []))
      .catch(() => setComplements([]));
  }, [id]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/products/${id}`),
      axios.get(`${API}/reviews/${id}`)
    ]).then(([prodRes, revRes]) => {
      setProduct(prodRes.data);
      setReviews(revRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (customer && !reviewForm.reviewer_name) {
      setReviewForm(f => ({ ...f, reviewer_name: `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || customer.email }));
    }
  }, [customer]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!customer) return;
    setSubmitError('');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/reviews`, { product_id: id, ...reviewForm });
      setReviews([res.data, ...reviews]);
      setReviewForm(f => ({ ...f, rating: 5, comment: '' }));
      reloadRatings();
    } catch (err) {
      setSubmitError('Erreur lors de la soumission. Réessayez.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-28 pb-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 animate-pulse">
          <div className="aspect-square bg-[#E8E2D8]" />
          <div className="space-y-4 pt-8">
            <div className="h-3 bg-[#E8E2D8] w-20" />
            <div className="h-10 bg-[#E8E2D8] w-64" />
            <div className="h-6 bg-[#E8E2D8] w-16" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) return <div className="pt-28 text-center text-black/40">Produit introuvable</div>;

  const avgRating = reviews.length > 0 ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length) : 0;
  const avgDisplay = reviews.length > 0 ? avgRating.toFixed(1) : null;

  return (
    <div data-testid="product-detail-page" className="pt-24 sm:pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/shop" data-testid="back-to-shop" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-black/40 hover:text-black transition-colors mb-8">
          <ArrowLeft size={14} /> Retour &agrave; la Boutique
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <ScrollReveal>
            <div className="relative aspect-square bg-[#EDE8DE] overflow-hidden sticky top-28 group cursor-zoom-in" data-testid="product-image-wrapper"
              onMouseMove={(e) => {
                const el = e.currentTarget.querySelector('img');
                if (!el) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const x = ((e.clientX - rect.left) / rect.width) * 100;
                const y = ((e.clientY - rect.top) / rect.height) * 100;
                el.style.transformOrigin = `${x}% ${y}%`;
              }}
            >
              <img
                src={resolveImage(product.image_url)}
                alt={product.name}
                className="product-image w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[2]"
              />
              <div className="absolute top-4 right-4 bg-black/80 text-white text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                Zoom
              </div>
              {product.best_seller && (
                <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5" style={{ backgroundColor: '#B80000', color: '#fff' }}>
                  Best-Seller
                </div>
              )}
            </div>
          </ScrollReveal>

          <div className="pt-0 lg:pt-8">
            <ScrollReveal delay={0.2}>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-black/40 mb-2">Peau {SKIN_TYPE_FR[product.skin_type] || product.skin_type}</p>
              <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {product.name}
              </h1>
              <div className="mb-6 flex items-baseline gap-3 flex-wrap">
                {product.original_price && product.original_price > product.price && (
                  <span className="text-lg text-black/35 line-through">{formatFCFA(product.original_price)}</span>
                )}
                <span className="text-2xl font-light">{formatFCFA(product.price)}</span>
                {product.original_price && product.original_price > product.price && (
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1" style={{ backgroundColor: '#B80000', color: '#fff' }}>
                    -{Math.round((1 - product.price / product.original_price) * 100)}%
                  </span>
                )}
              </div>
              <p className="text-base text-black/60 leading-relaxed mb-8">{product.description}</p>

              <button
                data-testid="add-to-cart-detail"
                onClick={() => addToCart(product.product_id)}
                className="btn-primary w-full sm:w-auto mb-10"
              >
                Ajouter au Panier
              </button>

              {product.stock < 10 && product.stock > 0 && (
                <p className="text-xs font-semibold mb-6" style={{ color: '#B80000' }}>Plus que {product.stock} en stock</p>
              )}

              {product.how_to_use && (
                <div className="border-t border-black/10 pt-8 mb-8">
                  <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-black mb-4">Mode d'Emploi</h3>
                  <p className="text-sm text-black/60 leading-relaxed">{product.how_to_use}</p>
                </div>
              )}

              {product.ingredients && product.ingredients.length > 0 && (
                <div className="border-t border-black/10 pt-6">
                  <button
                    data-testid="toggle-ingredients"
                    onClick={() => setShowIngredients(!showIngredients)}
                    className="flex items-center justify-between w-full py-2"
                  >
                    <span className="text-xs uppercase tracking-[0.2em] font-bold text-black">Ingr&eacute;dients</span>
                    {showIngredients ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  {showIngredients && (
                    <div className="pb-6 pt-2">
                      <div className="flex flex-wrap gap-2">
                        {product.ingredients.map((ing, i) => (
                          <span key={i} className="text-xs px-3 py-1.5 border border-black/10 text-black/60">
                            {ing}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Social share */}
              <div className="border-t border-black/10 pt-6 mt-4" data-testid="social-share-row">
                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-black/40 mb-3">Partager</p>
                <div className="flex items-center gap-2 flex-wrap">
                  {(() => {
                    const shareUrl = publicCurrentUrl();
                    const shareText = `${product.name} · Balmart`;
                    const openSocial = async (platform) => {
                      if (platform === 'instagram') {
                        if (typeof navigator !== 'undefined' && navigator.clipboard) {
                          await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
                          notify.success('Lien copié · collez-le dans votre story ou DM Instagram');
                        }
                        window.open('https://www.instagram.com/', '_blank');
                      } else if (platform === 'tiktok') {
                        if (typeof navigator !== 'undefined' && navigator.clipboard) {
                          await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
                          notify.success('Lien copié · collez-le dans votre bio ou message TikTok');
                        }
                        window.open('https://www.tiktok.com/', '_blank');
                      }
                    };
                    const social = [
                      { label: 'WhatsApp', kind: 'link', href: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`, testid: 'share-whatsapp' },
                      { label: 'Instagram', kind: 'action', onClick: () => openSocial('instagram'), testid: 'share-instagram' },
                      { label: 'TikTok', kind: 'action', onClick: () => openSocial('tiktok'), testid: 'share-tiktok' },
                      { label: 'Facebook', kind: 'link', href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, testid: 'share-facebook' },
                      { label: 'X', kind: 'link', href: `https://x.com/intent/post?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, testid: 'share-x' },
                    ];
                    return social.map(s => s.kind === 'link' ? (
                      <a
                        key={s.label}
                        href={s.href}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={s.testid}
                        className="text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-2 border border-black/15 hover:border-[#B80000] hover:text-[#B80000] transition-colors"
                      >
                        {s.label}
                      </a>
                    ) : (
                      <button
                        key={s.label}
                        type="button"
                        onClick={s.onClick}
                        data-testid={s.testid}
                        className="text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-2 border border-black/15 hover:border-[#B80000] hover:text-[#B80000] transition-colors"
                      >
                        {s.label}
                      </button>
                    ));
                  })()}
                  <button
                    data-testid="share-copy-link"
                    onClick={() => {
                      if (typeof navigator !== 'undefined' && navigator.clipboard) {
                        navigator.clipboard.writeText(publicCurrentUrl());
                        notify.success('Lien copié');
                      }
                    }}
                    className="text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-2 border border-black/15 hover:border-[#B80000] hover:text-[#B80000] transition-colors"
                  >
                    Copier le lien
                  </button>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>

        {/* Cross-sell: Complete your ritual */}
        {complements.length > 0 && (
          <section data-testid="ritual-complement-section" className="mt-24 border-t border-black/10 pt-16">
            <ScrollReveal>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={14} strokeWidth={1.5} style={{ color: '#B80000' }} />
                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-black/40">Rituel complet</p>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl font-light mb-12" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Complétez votre rituel
              </h2>
            </ScrollReveal>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
              {complements.map((p, i) => (
                <ScrollReveal key={p.product_id} delay={i * 0.1}>
                  <Link to={`/product/${p.product_id}`} data-testid={`complement-${p.product_id}`} className="group block">
                    <div className="aspect-square bg-[#EDE8DE] overflow-hidden mb-4">
                      <img
                        src={p.image_url}
                        alt={p.name}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    <h3 className="font-serif text-lg font-normal" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{p.name}</h3>
                    <p className="text-sm font-semibold mt-1" style={{ color: '#B80000' }}>{formatFCFA(p.price)}</p>
                  </Link>
                </ScrollReveal>
              ))}
            </div>
          </section>
        )}

        {/* Reviews Section */}
        <section data-testid="reviews-section" className="mt-24 border-t border-black/10 pt-16">
          <ScrollReveal>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-12 gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-black/40 mb-3">Avis Vérifiés</p>
                <h2 className="font-serif text-3xl sm:text-4xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Ce qu'ils en pensent
                </h2>
                {avgDisplay ? (
                  <div className="flex items-center gap-3 mt-3" data-testid="avg-rating-display">
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map(s => (
                        <Star
                          key={s}
                          size={16}
                          fill={s <= Math.round(avgRating) ? '#B80000' : 'none'}
                          stroke={s <= Math.round(avgRating) ? '#B80000' : '#00000030'}
                          strokeWidth={1.5}
                        />
                      ))}
                    </div>
                    <span className="text-sm font-semibold text-black">{avgDisplay}/5</span>
                    <span className="text-xs text-black/40">· {reviews.length} avis</span>
                  </div>
                ) : (
                  <p className="text-sm text-black/40 mt-3">Soyez le premier à laisser un avis</p>
                )}
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal>
            {customer ? (
              <form data-testid="review-form" onSubmit={submitReview} className="max-w-lg mb-16 space-y-5 p-6 border border-black/10 bg-[#EDE8DE]/40">
                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-black/60">Partager votre expérience</p>
                <input
                  data-testid="review-name-input"
                  type="text"
                  placeholder="Votre nom"
                  value={reviewForm.reviewer_name}
                  onChange={e => setReviewForm({...reviewForm, reviewer_name: e.target.value})}
                  required
                  className="bg-transparent border-b border-black/20 focus:border-black outline-none py-3 w-full text-sm transition-colors"
                />
                <div className="flex items-center gap-2">
                  <span className="text-xs uppercase tracking-[0.2em] font-bold text-black/40 mr-2">Note:</span>
                  {[1,2,3,4,5].map(star => (
                    <button
                      type="button"
                      key={star}
                      data-testid={`rating-star-${star}`}
                      onClick={() => setReviewForm({...reviewForm, rating: star})}
                      className="transition-transform hover:scale-110"
                    >
                      <Star
                        size={22}
                        fill={star <= reviewForm.rating ? '#B80000' : 'none'}
                        stroke={star <= reviewForm.rating ? '#B80000' : '#000'}
                        strokeWidth={1.5}
                      />
                    </button>
                  ))}
                </div>
                <textarea
                  data-testid="review-comment-input"
                  placeholder="Écrivez votre avis..."
                  value={reviewForm.comment}
                  onChange={e => setReviewForm({...reviewForm, comment: e.target.value})}
                  required
                  rows={4}
                  className="bg-transparent border-b border-black/20 focus:border-black outline-none py-3 w-full text-sm transition-colors resize-none"
                />
                {submitError && (
                  <p className="text-xs" style={{ color: '#B80000' }}>{submitError}</p>
                )}
                <button
                  data-testid="submit-review-btn"
                  type="submit"
                  disabled={submitting}
                  className="btn-primary text-xs disabled:opacity-50"
                >
                  {submitting ? 'Envoi…' : 'Soumettre l\u2019Avis'}
                </button>
              </form>
            ) : (
              <div data-testid="review-login-prompt" className="max-w-lg mb-16 p-6 border border-black/10 bg-[#EDE8DE]/40">
                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-black/60 mb-2">Partager votre avis</p>
                <p className="text-sm text-black/60 mb-4">
                  Connectez-vous pour laisser un avis vérifié sur ce produit.
                </p>
                <Link to="/login" data-testid="review-login-link" className="btn-primary text-xs inline-block">
                  Se connecter
                </Link>
              </div>
            )}
          </ScrollReveal>

          {reviews.length > 0 && (
            <div className="space-y-8 max-w-2xl">
              {reviews.map(review => (
                <ScrollReveal key={review.review_id}>
                  <div data-testid={`review-${review.review_id}`} className="border-b border-black/5 pb-8">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-sm">{review.reviewer_name}</span>
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map(s => (
                            <Star
                              key={s}
                              size={12}
                              fill={s <= review.rating ? '#B80000' : 'none'}
                              stroke={s <= review.rating ? '#B80000' : '#00000030'}
                              strokeWidth={1.5}
                            />
                          ))}
                        </div>
                      </div>
                      <span className="text-[10px] uppercase tracking-[0.2em] text-black/40">
                        {formatRelativeDate(review.created_at)}
                      </span>
                    </div>
                    <p className="text-sm text-black/60 leading-relaxed">{review.comment}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
